import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Input, ErrorBoundary } from "../common";
import { useAuth } from "../../contexts/AuthContext";
import { useLocalePath } from "../../hooks/useLocalePath";
import { useTranslation } from "../../i18n/I18nContext";
import { Icon } from "@iconify/react";
import { useRef, useState, useEffect, useMemo, useCallback } from "react";
import DayOffModal from "../modals/DayOffModal";

const MENU_STORAGE_KEY_PREFIX = "prombiz.owner.menu.pinned.v1";

function buildMenuStorageKey(user, business) {
  const ownerKey = user?.id || user?.email || "owner";
  const businessKey = business?.id || business?.slug || "default";
  return `${MENU_STORAGE_KEY_PREFIX}:${ownerKey}:${businessKey}`;
}

function useNavItems() {
  const { t, lang } = useTranslation();

  return useMemo(() => {
    /** Only routes wired to real backend APIs (no ComingSoon / owner-room scaffolds). */
    const items = [
      { labelKey: "business.overview", icon: "mdi:view-dashboard-outline", path: "/dashboard" },
      { labelKey: "business.myBusiness360", icon: "mdi:briefcase-account-outline", path: "/dashboard/my-business" },
      { labelKey: "business.services", icon: "mdi:briefcase-outline", path: "/dashboard/services" },
      { labelKey: "business.bookingSystem", icon: "mdi:calendar-clock-outline", path: "/dashboard/bookings" },
      { labelKey: "business.calendar", icon: "mdi:calendar-month-outline", path: "/dashboard/calendar" },
      { labelKey: "business.teamRoles", icon: "mdi:account-supervisor-outline", path: "/dashboard/team" },
      { labelKey: "business.crm", icon: "mdi:account-group-outline", path: "/dashboard/crm" },
      { labelKey: "business.inboxChat", icon: "mdi:message-processing-outline", path: "/dashboard/inbox" },
      { labelKey: "communicationHub.channels", icon: "mdi:connection", path: "/dashboard/channels" },
      { labelKey: "business.reviews", icon: "mdi:star-outline", path: "/dashboard/reviews" },
      { labelKey: "business.orders", icon: "mdi:cart-outline", path: "/dashboard/orders" },
      { labelKey: "business.analytics", icon: "mdi:chart-line", path: "/dashboard/analytics" },
      { labelKey: "business.notifications", icon: "mdi:bell-outline", path: "/dashboard/notifications" },
      { labelKey: "business.documents", icon: "mdi:file-cabinet", path: "/dashboard/documents" },
      { label: "Invoices", icon: "mdi:file-invoice-outline", path: "/dashboard/invoice" },
    ];

    const sections = [
      { title: null, items: [items[0]] },
      { title: t("business.sectionPresence"), items: [items[1]] },
      { title: t("business.sectionSales"), items: [items[2], items[3]] },
      { title: t("business.sectionSales2") || "Sales", items: [items[10]] },
      { title: t("business.sectionOperations"), items: [items[4], items[5], items[6], items[7], items[8], items[11], items[12], items[13], items[14]] },
    ];

    const defaultPinnedPaths = [
      "/dashboard",
      "/dashboard/my-business",
      "/dashboard/services",
      "/dashboard/bookings",
      "/dashboard/inbox",
    ];

    return { items, sections, defaultPinnedPaths };
  }, [t, lang]);
}

const DashboardShell = () => {
  const { user, logout } = useAuth();
  const { t, lang, setLang, supportedLangs } = useTranslation();
  const { pathname } = useLocation();
  const lp = useLocalePath();
  const navigate = useNavigate();
  const { items: ALL_NAV_ITEMS, sections: BASE_NAV_SECTIONS, defaultPinnedPaths } = useNavItems();

  // Use enriched businesses array from auth context
  const allBusinesses = [
    ...(user?.businesses || []),
    ...(user?.memberships || []).map(m => ({ id: m.businessId, name: m.businessName, slug: m.businessSlug, role: m.role })),
  ];
  const BUSINESSES = allBusinesses.length > 0 ? allBusinesses : user?.business ? [user.business] : [];
  const [activeBusiness, setActiveBusiness] = useState(BUSINESSES[0] || { name: user?.firstName || "My Business" });
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const switcherRef = useRef(null);
  const userMenuRef = useRef(null);
  const [menuConfiguratorOpen, setMenuConfiguratorOpen] = useState(false);
  const [pinnedPaths, setPinnedPaths] = useState(defaultPinnedPaths);
  const [dayOffModalOpen, setDayOffModalOpen] = useState(false);

  const menuStorageKey = useMemo(() => buildMenuStorageKey(user, activeBusiness), [
    user?.id,
    user?.email,
    activeBusiness?.id,
    activeBusiness?.slug,
  ]);

  const togglePinnedPath = useCallback((path) => {
    setPinnedPaths((prev) =>
      prev.includes(path) ? prev.filter((itemPath) => itemPath !== path) : [...prev, path]
    );
  }, []);

  const pinnedItems = useMemo(() => {
    const byPath = new Map(ALL_NAV_ITEMS.map((item) => [item.path, item]));
    return pinnedPaths.map((path) => byPath.get(path)).filter(Boolean);
  }, [ALL_NAV_ITEMS, pinnedPaths]);

  const NAV_SECTIONS = useMemo(() => {
    const pinnedSet = new Set(pinnedPaths);
    const sectionsWithoutPinned = BASE_NAV_SECTIONS
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => !pinnedSet.has(item.path)),
      }))
      .filter((section) => section.items.length > 0);

    if (pinnedItems.length === 0) {
      return sectionsWithoutPinned;
    }

    return [{ title: "My Menu", items: pinnedItems }, ...sectionsWithoutPinned];
  }, [BASE_NAV_SECTIONS, pinnedItems, pinnedPaths]);

  useEffect(() => {
    const validPaths = new Set(ALL_NAV_ITEMS.map((item) => item.path));

    try {
      const raw = window.localStorage.getItem(menuStorageKey);
      if (!raw) {
        setPinnedPaths(defaultPinnedPaths.filter((path) => validPaths.has(path)));
        return;
      }

      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) {
        setPinnedPaths(defaultPinnedPaths.filter((path) => validPaths.has(path)));
        return;
      }

      const normalized = parsed.filter((path) => typeof path === "string" && validPaths.has(path));
      setPinnedPaths(normalized.length > 0 ? normalized : defaultPinnedPaths.filter((path) => validPaths.has(path)));
    } catch {
      setPinnedPaths(defaultPinnedPaths.filter((path) => validPaths.has(path)));
    }
  }, [menuStorageKey, ALL_NAV_ITEMS, defaultPinnedPaths]);

  useEffect(() => {
    try {
      window.localStorage.setItem(menuStorageKey, JSON.stringify(pinnedPaths));
    } catch {
      // Ignore localStorage write errors (private mode / quota)
    }
  }, [menuStorageKey, pinnedPaths]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate(lp("/login"));
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (switcherRef.current && !switcherRef.current.contains(e.target)) {
        setSwitcherOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const displayName =
    user?.business?.name ||
    user?.firstName ||
    user?.name ||
    activeBusiness.name;

  const isNavActive = useCallback(
    (path) => {
      const localePath = lp(path);
      return pathname === localePath || (path !== "/dashboard" && pathname.startsWith(localePath));
    },
    [pathname, lp]
  );

  return (
    <div className="bg-[#F4F5FA] min-h-screen flex flex-row items-stretch">

      {/* ---- Sidebar ---- */}
      <aside className="lg:w-[260px] w-[240px] bg-white border-r border-[#D4D6FF] flex flex-col flex-shrink-0 min-h-screen">

        {/* Logo */}
        <div className="px-5 pt-5 pb-3 flex-shrink-0">
          <Link to={lp("/")}>
            <img
              src="/icons/logo_horizontal.webp"
              alt="logo"
              className="w-[130px] h-[28px] object-cover"
            />
          </Link>
        </div>

        {/* Multi-business Switcher */}
        <div className="px-3 pb-3 flex-shrink-0 relative" ref={switcherRef}>
          <button
            onClick={() => setSwitcherOpen((v) => !v)}
            className="w-full flex flex-row items-center gap-3 p-3 rounded-[10px] border border-[#E3E3E3] hover:border-primary transition-colors text-left"
          >
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs font-bold">
                {displayName.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-dark-blue truncate">{displayName}</p>
              <p className="text-[11px] text-[#6A7282]">{activeBusiness.type}</p>
            </div>
            <Icon
              icon={switcherOpen ? "mdi:chevron-up" : "mdi:chevron-down"}
              className="w-4 h-4 text-[#6A7282] flex-shrink-0 transition-transform duration-200"
            />
          </button>

          {switcherOpen && (
            <div className="absolute top-full left-3 right-3 mt-1 bg-white border border-[#E3E3E3] rounded-[12px] shadow-lg z-50 overflow-hidden">
              {BUSINESSES.map((biz) => (
                <button
                  key={biz.id}
                  onClick={() => { setActiveBusiness(biz); setSwitcherOpen(false); }}
                  className={`w-full flex flex-row items-center gap-3 px-4 py-3 hover:bg-[#F4F5FA] transition-colors text-left ${
                    activeBusiness.id === biz.id ? "bg-[#F4F5FA]" : ""
                  }`}
                >
                  <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-primary text-xs font-bold">{biz.name.charAt(0)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-dark-blue truncate">{biz.name}</p>
                    <p className="text-[11px] text-[#6A7282]">{biz.type}</p>
                  </div>
                  {activeBusiness.id === biz.id && (
                    <Icon icon="mdi:check" className="w-4 h-4 text-primary flex-shrink-0" />
                  )}
                </button>
              ))}
              <div className="border-t border-[#E3E3E3]">
                <button className="w-full flex flex-row items-center gap-3 px-4 py-3 hover:bg-[#F4F5FA] transition-colors text-left">
                  <div className="w-7 h-7 rounded-lg border-2 border-dashed border-[#D4D6FF] flex items-center justify-center flex-shrink-0">
                    <Icon icon="mdi:plus" className="w-4 h-4 text-primary" />
                  </div>
                  <span className="text-sm font-medium text-primary">{t("business.addBusiness")}</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 pb-4 flex flex-col gap-0 overflow-y-auto">
          <div className="mb-3 px-1">
            <button
              onClick={() => setMenuConfiguratorOpen((prev) => !prev)}
              className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-[8px] border border-[#E3E3E3] text-xs font-semibold text-[#4A5565] hover:border-primary hover:text-primary transition-colors"
            >
              <span>Customize menu</span>
              <Icon icon={menuConfiguratorOpen ? "mdi:chevron-up" : "mdi:chevron-down"} className="w-4 h-4" />
            </button>

            {menuConfiguratorOpen && (
              <div className="mt-2 p-3 rounded-[10px] border border-[#E3E3E3] bg-[#FAFAFD]">
                <p className="text-[11px] text-[#6A7282] mb-2">Select modules to pin at the top of the sidebar.</p>
                <div className="max-h-52 overflow-y-auto pr-1 flex flex-col gap-1.5">
                  {ALL_NAV_ITEMS.map((item) => {
                    const checked = pinnedPaths.includes(item.path);
                    return (
                      <label key={`cfg-${item.path}`} className="flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-white">
                        <span className="text-xs text-[#4A5565] truncate">{t(item.labelKey)}</span>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {item.inDevelopment && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">In Dev</span>
                          )}
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => togglePinnedPath(item.path)}
                            className="w-4 h-4 accent-primary"
                          />
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {NAV_SECTIONS.map((section, si) => (
            <div key={si} className={si > 0 ? "mt-3" : ""}>
              {section.title && (
                <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-wider text-[#9CA3AF]">
                  {section.title}
                </p>
              )}
              <div className="flex flex-col gap-0.5">
                {section.items.map((mi) => {
                  const active = isNavActive(mi.path);
                  const hasChildren = mi.children?.length > 0;
                  return (
                    <div key={mi.path}>
                      <Link
                        to={lp(mi.path)}
                        className={`flex flex-row items-center gap-3 px-3 py-2.5 rounded-[8px] text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                          active
                            ? "bg-[linear-gradient(90deg,#1B51EC_-10.39%,#1B51EC_103.51%)] text-white"
                            : "text-[#4A5565] hover:text-primary hover:bg-[#F4F5FA]"
                        }`}
                      >
                        <Icon icon={mi.icon} className="w-4 h-4 flex-shrink-0" />
                        <span className="flex-1">{mi.labelKey ? t(mi.labelKey) : mi.label}</span>
                        {hasChildren && (
                          <Icon icon={active ? "mdi:chevron-down" : "mdi:chevron-right"} className="w-4 h-4 flex-shrink-0 opacity-50" />
                        )}
                        {mi.inDevelopment && (
                          <span className={`text-[10px] px-2 py-0.5 rounded-full flex-shrink-0 ${active ? "bg-white/20 text-white" : "bg-amber-100 text-amber-700"}`}>In Dev</span>
                        )}
                        {mi.badge && !active && (
                          <span className="w-2 h-2 rounded-full bg-[#FB2C36] flex-shrink-0" />
                        )}
                      </Link>
                      {hasChildren && active && (
                        <div className="ml-4 mt-0.5 mb-1 flex flex-col gap-0.5 border-l-2 border-blue-100 pl-3">
                          {mi.children.map((child) => {
                            const tierColors = { essential: "text-emerald-500", enhanced: "text-blue-500", advanced: "text-purple-500" };
                            return (
                              <Link
                                key={child.path}
                                to={lp(child.path)}
                                className="flex items-center gap-2 px-2 py-1.5 rounded-md text-xs font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors"
                              >
                                <Icon icon="mdi:check-circle" className={`w-3.5 h-3.5 ${tierColors[child.tier] || "text-gray-300"}`} />
                                <span className="truncate">{child.label}</span>
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* User footer */}
        <div className="p-3 border-t border-[#E5E7EB] flex-shrink-0 flex flex-col gap-1">
          <div className="flex flex-row items-center gap-3 px-2 py-2">
            <div className="w-8 h-8 rounded-full bg-primary/10 border border-primary flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-semibold text-primary">
                {(user?.firstName?.[0] || user?.name?.[0] || "U").toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-dark-blue truncate">
                {user?.firstName || user?.name || t("business.businessOwner")}
              </p>
              <p className="text-[10px] text-[#6A7282] truncate">{user?.email || ""}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex flex-row items-center gap-2 px-3 py-2 rounded-[8px] text-sm font-medium text-red-500 hover:bg-red-50 transition-colors"
          >
            <Icon icon="mdi:logout" className="w-4 h-4 flex-shrink-0" />
            <span>{t("business.logout")}</span>
          </button>
        </div>
      </aside>

      {/* ---- Main ---- */}
      <main className="flex-1 p-6 flex flex-col gap-6 min-w-0">
        <div className="flex flex-row items-center justify-between">
          <Input
            variant="transparent"
            placeholder={t("business.searchPlaceholder")}
            inputIcon="mdi:search"
            containerClassName="lg:w-[460px]"
            className="px-[15px] py-3 text-sm"
            inputIconClassName="text-[#99A1AF]"
          />

          <div className="flex flex-row items-center gap-4">
            {/* Language switcher */}
            <div className="flex items-center bg-[#F4F5FA] rounded-lg p-0.5 gap-0.5">
              {(supportedLangs || ["en", "ru", "he"]).map((l) => (
                <button
                  key={l}
                  onClick={() => {
                    setLang(l);
                    // Replace locale prefix in URL and navigate
                    const path = pathname.replace(/^\/(en|ru|he)/, `/${l}`);
                    navigate(path);
                  }}
                  className={`px-2 py-1 rounded-md text-[11px] font-semibold uppercase transition-all ${
                    lang === l
                      ? "bg-white text-primary shadow-sm"
                      : "text-[#6A7282] hover:text-gray-900"
                  }`}
                >
                  {l === "he" ? "עב" : l.toUpperCase()}
                </button>
              ))}
            </div>

            {/* Day off button */}
            <button
              onClick={() => setDayOffModalOpen(true)}
              className="relative p-2 rounded-lg hover:bg-amber-50 transition-colors"
              title={lang === "ru" ? "Добавить выходной" : lang === "he" ? "הוסף יום חופש" : "Add day off"}
            >
              <Icon icon="mdi:calendar-remove" className="w-4 h-4 text-gray-500" />
            </button>

            <Link
              to={lp("/dashboard/notifications")}
              className="relative w-8 h-8 flex items-center justify-center rounded-full hover:bg-white hover:shadow transition-all"
            >
              <Icon icon="mdi:bell-outline" className="w-4 h-4 text-[#374151]" />
              <span className="absolute size-2 rounded-full bg-[#FB2C36] top-1 right-1" />
            </Link>
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen((v) => !v)}
                className="relative flex flex-row items-center gap-2"
              >
                <div className="w-8 h-8 rounded-full flex items-center justify-center bg-primary/10 border border-primary">
                  <span className="text-xs font-semibold text-primary">
                    {(user?.firstName?.[0] || user?.name?.[0] || "B").toUpperCase()}
                  </span>
                </div>
                <Icon icon="mdi:chevron-down" className="w-4 h-4 text-[#374151]" />
              </button>
              {userMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-[12px] shadow-lg border border-[#E5E7EB] z-50 overflow-hidden">
                  <div className="px-4 py-3 border-b border-[#E5E7EB]">
                    <p className="text-sm font-semibold text-dark-blue truncate">
                      {user?.firstName || user?.name || "User"}
                    </p>
                    <p className="text-xs text-[#6A7282] truncate">{user?.email}</p>
                  </div>
                  <Link
                    to={lp("/dashboard/settings")}
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#374151] hover:bg-[#F4F5FA] transition-colors"
                  >
                    <Icon icon="mdi:cog-outline" className="w-4 h-4" />
                    {t("business.settings")}
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors"
                  >
                    <Icon icon="mdi:logout" className="w-4 h-4" />
                    {t("business.logout")}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        <ErrorBoundary
          fallback={
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center max-w-md">
                <p className="text-4xl mb-4 opacity-60">&#9888;</p>
                <h2 className="text-xl font-semibold text-gray-800 mb-2">
                  Something went wrong
                </h2>
                <p className="text-sm text-gray-500 mb-6">
                  An error occurred in this section. The rest of the dashboard is still available.
                </p>
                <button
                  onClick={() => window.location.reload()}
                  className="px-6 py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
                >
                  Reload page
                </button>
              </div>
            </div>
          }
        >
          <Outlet />
        </ErrorBoundary>
      </main>

      <DayOffModal
        isOpen={dayOffModalOpen}
        onClose={() => setDayOffModalOpen(false)}
        businessId={activeBusiness?.id}
        onCreated={() => {}}
      />
    </div>
  );
};

export default DashboardShell;







